import { Button } from "react-aria-components";
import { useIntl } from "react-intl";
import type { Workspace } from "./data.ts";

/** An emoji icon, or the name's first letter; an uploaded one (`file:`) is not shown here. */
export const workspaceIcon = (w: Pick<Workspace, "icon" | "name">) =>
  w.icon && !w.icon.startsWith("file:") ? w.icon : (w.name.trim()[0] ?? "").toUpperCase();

/**
 * The workspace switcher's button (ADR-0098), at the top of the sidebar: the workspace's icon,
 * name and main address. Its menu (SwitcherMenu.tsx) loads after the sidebar; until then this
 * button is drawn alone. Its name is what it shows, then what it does (WCAG 2.5.3); the icon and
 * the arrow are drawn by the stylesheet, from `data-icon`, and are not part of it.
 */
export function SwitcherButton({
  workspace,
  onPress,
}: {
  workspace: Workspace;
  /** Before the menu has loaded (MenuTrigger handles presses after). */
  onPress?: () => void;
}) {
  const intl = useIntl();
  return (
    <Button className="workspace-switcher" {...(onPress ? { onPress } : {})}>
      <span
        className="workspace-switcher-icon"
        data-icon={workspaceIcon(workspace)}
        aria-hidden="true"
      />
      <span className="workspace-switcher-text">
        <span className="workspace-switcher-name">{workspace.name}</span>{" "}
        {workspace.address && (
          <span className="workspace-switcher-address">{workspace.address}</span>
        )}
      </span>{" "}
      <span className="visually-hidden">{intl.formatMessage({ id: "sidebar.switcher" })}</span>
      <span className="workspace-switcher-chevron" aria-hidden="true" />
    </Button>
  );
}
